// Stripe on the Money screen (M3 plan D1, D10): connect an account with a read-only key, see when it
// last synced and its balance, sync now. The key is never shown again after saving.
import { type FormEvent, useEffect, useState } from 'react';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';
import { day, dollars, fromCents } from '../format.js';

interface Connection {
  id: string;
  name: string;
  key_hint: string;
  sync_from: string;
  last_sync_at: string | null;
  last_sync_status: 'running' | 'ok' | 'failed' | null;
  last_sync_error: string | null;
  balance_minor: string | null;
  product: string | null;
  entries: number;
  flags: number;
  check: {
    books_minor: string;
    stripe_minor: string;
    in_transit_minor: string;
    difference_minor: string;
  } | null;
}

export function StripeSection({ api, orgId }: { api: Api; orgId: string }) {
  const list = useOrgData<{ connections: Connection[] }>(api, orgId, '/stripe');
  const [adding, setAdding] = useState(false);
  const connections = list.data?.connections ?? [];
  const running = connections.some((c) => c.last_sync_status === 'running');

  // A sync runs in the background; check back every few seconds until it is done.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void list.reload(), 4000);
    return () => clearInterval(timer);
  }, [running, list.reload]);

  if (list.error) return <p className="error">{list.error}</p>;
  if (!list.data) return null;
  return (
    <section className="stack panel">
      <h2>Stripe</h2>
      {connections.map((c) => (
        <StripeCard key={c.id} api={api} orgId={orgId} connection={c} onChanged={list.reload} />
      ))}
      {connections.length === 0 || adding ? (
        <ConnectStripe
          api={api}
          orgId={orgId}
          first={connections.length === 0}
          onConnected={() => {
            setAdding(false);
            void list.reload();
          }}
        />
      ) : (
        <button type="button" className="link" onClick={() => setAdding(true)}>
          Connect another Stripe account
        </button>
      )}
    </section>
  );
}

function StripeCard(props: {
  api: Api;
  orgId: string;
  connection: Connection;
  onChanged: () => void;
}) {
  const { api, orgId, connection: c } = props;
  const [error, setError] = useState('');
  async function syncNow() {
    setError('');
    try {
      await api.send(orgId, 'POST', `/stripe/${c.id}/sync`);
      props.onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the sync.');
    }
  }
  return (
    <div className="stack bank-card">
      <div className="title-row">
        <h2>
          {c.name}
          {c.product && <span className="muted"> for {c.product}</span>}
        </h2>
        {c.flags > 0 && <span className="pill">{c.flags} to check</span>}
      </div>
      <p className="muted">{status(c)}</p>
      {c.last_sync_status === 'failed' && c.last_sync_error && (
        <p className="error">{c.last_sync_error}</p>
      )}
      {c.check && <StripeCheck check={c.check} />}
      {c.balance_minor !== null && (
        <p className="muted">
          {c.entries} transactions recorded since {day(c.sync_from)}.
        </p>
      )}
      <button type="button" disabled={c.last_sync_status === 'running'} onClick={syncNow}>
        {c.last_sync_status === 'running' ? 'Syncing' : 'Sync now'}
      </button>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Plan D9: the books against Stripe, with payouts still on their way to the bank. */
function StripeCheck({ check }: { check: NonNullable<Connection['check']> }) {
  const money = (minor: string) => dollars(fromCents(minor));
  const transit =
    check.in_transit_minor !== '0'
      ? `, plus ${money(check.in_transit_minor)} on its way to your bank`
      : '';
  if (check.difference_minor === '0')
    return (
      <p>
        Matches Stripe: balance {money(check.stripe_minor)}
        {transit}.
      </p>
    );
  return (
    <p className="error">
      Your books show {money(check.books_minor)} for this Stripe account, but Stripe shows{' '}
      {money(check.stripe_minor)}
      {transit}. Difference {money(check.difference_minor)}.
    </p>
  );
}

function status(c: Connection): string {
  if (c.last_sync_status === 'running') return 'Syncing with Stripe now.';
  if (!c.last_sync_at) return 'Not synced yet.';
  const when = new Date(c.last_sync_at).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
  return c.last_sync_status === 'failed'
    ? `The last sync, ${when}, did not finish.`
    : `Synced ${when}. Foderis syncs again every night.`;
}

function ConnectStripe(props: {
  api: Api;
  orgId: string;
  first: boolean;
  onConnected: () => void;
}) {
  const { api, orgId } = props;
  const tracking = useOrgData<{ dimensions: { values: { id: string; name: string }[] }[] }>(
    api,
    orgId,
    '/tracking',
  );
  const products = tracking.data?.dimensions[0]?.values ?? [];
  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [key, setKey] = useState('');
  const [syncFrom, setSyncFrom] = useState(firstOfThisMonth());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function connect(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', '/stripe', {
        name: name || products.find((p) => p.id === productId)?.name,
        key,
        ...(productId ? { productValueId: productId } : {}),
        syncFrom,
      });
      setKey('');
      props.onConnected();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect Stripe.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={connect} className="stack">
      {!props.first && <h2>Connect a Stripe account</h2>}
      <p className="muted">
        Foderis reads your Stripe sales, fees, refunds and payouts every night and records them for
        you. In Stripe, go to Developers, API keys, Create restricted key, and give it Read access
        to Balance, Balance transactions, Charges, Customers, Disputes, Payouts, Refunds, Payment
        intents and Subscriptions. Leave everything else at None.
      </p>
      <label>
        Which product does this Stripe account sell?
        <select value={productId} onChange={(e) => setProductId(e.target.value)} required>
          <option value="">Choose</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Restricted key (starts with rk_live_)
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          onChange={(e) => setKey(e.target.value.trim())}
          required
        />
      </label>
      <label>
        Start from
        <input
          type="date"
          value={syncFrom}
          onChange={(e) => setSyncFrom(e.target.value)}
          required
        />
      </label>
      <label>
        Name (optional)
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={products.find((p) => p.id === productId)?.name}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy || !key || !productId}>
        {busy ? 'Checking the key with Stripe' : 'Connect'}
      </button>
    </form>
  );
}

function firstOfThisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}
